import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getSyncTemplates } from '@/services/api';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    Plus, ArrowLeft, MoreVertical, Play, Pause,
    Trash2, FileText, Zap, Shield, RefreshCw, Settings2, TrendingUp
} from 'lucide-react';
import type { SyncTemplate } from '@/types/price-rules';
import CreateTemplateModal from './components/CreateTemplateModal';
import priceRulesClient from "@/services/api/price-rules.ts";

export default function Templates() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState<number | null>(null);

    const { data: templates, isLoading, refetch } = useQuery({
        queryKey: ['sync-templates'],
        queryFn: getSyncTemplates,
    });

    const activateMutation = useMutation({
        mutationFn: (templateId: number) => priceRulesClient.post(`/api/v1/sync-templates/${templateId}/activate`),
        onSuccess: () => {
            toast({ title: 'Template ativado!' });
            queryClient.invalidateQueries({ queryKey: ['sync-templates'] });
        },
        onError: () => toast({ variant: 'destructive', title: 'Erro ao ativar template' }),
    });

    const deactivateMutation = useMutation({
        mutationFn: (templateId: number) => priceRulesClient.post(`/api/v1/sync-templates/${templateId}/deactivate`),
        onSuccess: () => {
            toast({ title: 'Template desativado!' });
            queryClient.invalidateQueries({ queryKey: ['sync-templates'] });
        },
        onError: () => toast({ variant: 'destructive', title: 'Erro ao desativar template' }),
    });

    const deleteMutation = useMutation({
        mutationFn: (templateId: number) => priceRulesClient.delete(`/api/v1/sync-templates/${templateId}`),
        onSuccess: () => {
            toast({ title: 'Template excluído!' });
            queryClient.invalidateQueries({ queryKey: ['sync-templates'] });
            setOpenMenu(null);
        },
        onError: (error: any) => toast({
            variant: 'destructive',
            title: 'Erro ao excluir',
            description: error.response?.data?.detail || 'Não foi possível excluir.'
        }),
    });

    const handleAction = (action: string, templateId: number) => {
        setOpenMenu(null);
        if (action === 'activate') activateMutation.mutate(templateId);
        if (action === 'deactivate') deactivateMutation.mutate(templateId);
        if (action === 'delete') deleteMutation.mutate(templateId);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/price-rules')}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Templates de Sync</h1>
                        <p className="text-sm text-gray-500">Configure como as regras são sincronizadas automaticamente</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-10 px-3">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button onClick={() => setCreateModalOpen(true)} className="h-10 bg-blue-600 hover:bg-blue-700">
                        <Plus className="mr-2 h-4 w-4" />
                        Novo Template
                    </Button>
                </div>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-gray-600">Carregando...</span>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!isLoading && templates?.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                        <FileText className="h-8 w-8 text-purple-500" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">Nenhum template</h3>
                    <p className="mt-1 text-sm text-gray-500">Crie seu primeiro template de sincronização</p>
                    <Button className="mt-4" onClick={() => setCreateModalOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Criar Template
                    </Button>
                </div>
            )}

            {/* Templates Grid */}
            {templates && templates.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates.map((template: SyncTemplate) => (
                        <div
                            key={template.id}
                            className={`bg-white rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                                template.status === 'active' ? 'border-emerald-200' : 'border-gray-200'
                            }`}
                        >
                            {/* Card Header */}
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                template.status === 'active'
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                {template.status === 'active' ? '● Ativo' : '○ Inativo'}
                                            </span>
                                        </div>
                                        {template.description && (
                                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                                        )}
                                    </div>

                                    {/* Dropdown Menu */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setOpenMenu(openMenu === template.id ? null : template.id)}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <MoreVertical className="h-4 w-4" />
                                        </button>

                                        {openMenu === template.id && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                                                <div className="absolute z-20 right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                    {template.status !== 'active' ? (
                                                        <button
                                                            onClick={() => handleAction('activate', template.id)}
                                                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                                                        >
                                                            <Play className="h-4 w-4 text-emerald-500" />
                                                            Ativar
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleAction('deactivate', template.id)}
                                                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                                                        >
                                                            <Pause className="h-4 w-4 text-yellow-500" />
                                                            Desativar
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleAction('delete', template.id)}
                                                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                        Excluir
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Mode Badge */}
                                <div className="mt-3">
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg ${
                                        template.sync_mode === 'automatic'
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {template.sync_mode === 'automatic' ? (
                                            <>
                                                <Zap className="h-3 w-3" />
                                                Automático
                                            </>
                                        ) : (
                                            <>
                                                <Settings2 className="h-3 w-3" />
                                                Manual
                                            </>
                                        )}
                                    </span>
                                </div>

                                {/* Stats */}
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg">
                                        <TrendingUp className="h-4 w-4 text-amber-600" />
                                        <div>
                                            <p className="text-xs text-amber-600">Thresholds</p>
                                            <p className="text-sm font-bold text-amber-700">{template.scale_thresholds.length}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg">
                                        <Shield className="h-4 w-4 text-blue-600" />
                                        <div>
                                            <p className="text-xs text-blue-600">Guards</p>
                                            <p className="text-sm font-bold text-blue-700">{template.guard_conditions.length}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Thresholds Preview */}
                                {template.scale_thresholds.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-xs font-medium text-gray-500 mb-2">Escalonamento:</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {template.scale_thresholds
                                                .sort((a, b) => a.difference_percentage - b.difference_percentage)
                                                .map((t, i) => (
                                                    <span
                                                        key={i}
                                                        className={`px-2 py-1 text-xs font-medium rounded-lg ${
                                                            i === 0 ? 'bg-emerald-100 text-emerald-700' :
                                                                i === 1 ? 'bg-blue-100 text-blue-700' :
                                                                    i === 2 ? 'bg-amber-100 text-amber-700' :
                                                                        'bg-red-100 text-red-700'
                                                        }`}
                                                    >
                                                        ≥{t.difference_percentage}% → ±{t.scale_percentage}%
                                                    </span>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Card Footer */}
                            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                                <span className="text-sm text-gray-500">
                                    {template.rules_count} regra{template.rules_count !== 1 ? 's' : ''} vinculada{template.rules_count !== 1 ? 's' : ''}
                                </span>
                                {template.rules_count > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-emerald-600">
                                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                        Em uso
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <CreateTemplateModal
                open={createModalOpen}
                onOpenChange={setCreateModalOpen}
            />
        </div>
    );
}